/**
 * Mesaj Formatleyici (Visitor tarafı - sadece parse)
 * Kullanım: formatter.platform("whatsapp").parse(message)
 */

// Platform formatları tanımları
const PLATFORM_FORMATS = {
    whatsapp: {
        bold: (text) => `*${text}*`,
        italic: (text) => `_${text}_`,
        strikethrough: (text) => `~${text}~`,
        code: (text) => `\`${text}\``,
        monospace: (text) => `\`\`\`${text}\`\`\``,
        blockquote: (text) => `> ${text}`,
    },
    instagram: {
        bold: (text) => `*${text}*`,
        italic: (text) => `_${text}_`,
        strikethrough: (text) => `~${text}~`,
        code: (text) => `\`${text}\``,
        monospace: (text) => `\`\`\`${text}\`\`\``,
        blockquote: (text) => `> ${text}`,
    },
    messenger: {
        bold: (text) => `"${text}"`,
        italic: (text) => `_${text}_`,
        strikethrough: (text) => `~${text}~`,
        code: (text) => `\`${text}\``,
        monospace: (text) => `\`\`\`${text}\`\`\``,
        blockquote: (text) => `> ${text}`,
    },
};

class MessageFormatter {
    constructor() {
        this.selectedPlatform = null;
    }

    platform(platformName) {
        this.selectedPlatform = platformName;
        return this;
    }

    // Platform formatından HTML'e dönüştür
    parse(message) {
        if (!message) return '';
        if (!this.selectedPlatform) return message;

        const parsers = {
            whatsapp: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp),
            web: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp),
            instagram: () => this.parseTextFormat(message, PLATFORM_FORMATS.instagram),
            telegram: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp),
            messenger: () => this.parseTextFormat(message, PLATFORM_FORMATS.messenger),
            trendyol: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp),
            linkedin: () => this.parseTextFormat(message, PLATFORM_FORMATS.whatsapp),
        };

        const parser = parsers[this.selectedPlatform];
        return parser ? parser() : message;
    }

    // Platform formatını HTML'e çevir
    parseTextFormat(text, formatRules) {
        // String'i URL ve non-URL parçalara ayır
        const urlRegex = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;
        const parts = [];
        let lastIndex = 0;
        let match;

        // URL'leri bul ve string'i parçalara ayır
        while ((match = urlRegex.exec(text)) !== null) {
            // URL'den önceki kısım (non-URL)
            if (match.index > lastIndex) {
                parts.push({
                    type: 'text',
                    content: text.substring(lastIndex, match.index)
                });
            }
            // URL kısmı
            parts.push({
                type: 'url',
                content: match[0]
            });
            lastIndex = match.index + match[0].length;
        }

        // Kalan kısım (non-URL)
        if (lastIndex < text.length) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex)
            });
        }

        // Eğer hiç URL yoksa tüm text'i ekle
        if (parts.length === 0) {
            parts.push({
                type: 'text',
                content: text
            });
        }

        // Sadece non-URL parçalarda formatlama yap
        const formattedParts = parts.map(part => {
            if (part.type === 'url') {
                return part.content; // URL'leri olduğu gibi bırak
            }

            let result = part.content;

            // Blockquote: > text -> <blockquote>text</blockquote>
            if (formatRules.blockquote) {
                result = result.replace(/^>\s+(.+)$/gm, '<blockquote>$1</blockquote>');
            }

            // Monospace: ```text``` -> <pre>text</pre>
            if (formatRules.monospace) {
                result = result.replace(/```(.+?)```/gs, '<pre>$1</pre>');
            }

            // Code: `text` -> <code>text</code>
            if (formatRules.code) {
                result = result.replace(/`(.+?)`/g, '<code>$1</code>');
            }

            // İç içe formatlar için recursive parse
            let hasChange = true;
            let iterations = 0;
            const maxIterations = 10;

            while (hasChange && iterations < maxIterations) {
                const before = result;
                iterations++;

                // Strikethrough: ~text~ -> <s>text</s>
                if (formatRules.strikethrough) {
                    result = result.replace(/(?<![a-zA-Z0-9])~(.+?)~(?![a-zA-Z0-9])/g, '<s>$1</s>');
                }

                // Italic: _text_ -> <i>text</i>
                if (formatRules.italic) {
                    result = result.replace(/(?<![a-zA-Z0-9])_(.+?)_(?![a-zA-Z0-9])/g, '<i>$1</i>');
                }

                // Bold: *text* veya "text" -> <b>text</b>
                if (formatRules.bold) {
                    if (formatRules.bold('x') === '*x*') {
                        result = result.replace(/(?<![a-zA-Z0-9])\*(.+?)\*(?![a-zA-Z0-9])/g, '<b>$1</b>');
                    } else if (formatRules.bold('x') === '"x"') {
                        result = result.replace(/(?<![a-zA-Z0-9])"(.+?)"(?![a-zA-Z0-9])/g, '<b>$1</b>');
                    }
                }

                hasChange = before !== result;
            }

            return result;
        });

        // Tüm parçaları birleştir
        var finalResult = formattedParts.join('');

        return this.normalizeLineBreaksForDisplay(finalResult);
    }

    // Panelde satır sonlarını düzgün göstermek için newline ve block elementleri <br>'ye çevir
    normalizeLineBreaksForDisplay(text) {
        if (!text) return text;

        var protectedBlocks = [];
        text = text.replace(/<(pre|code)[^>]*>[\s\S]*?<\/\1>/gi, function(match) {
            var index = protectedBlocks.length;
            protectedBlocks.push(match);
            return '\x00BLOCK' + index + '\x00';
        });

        text = text.replace(/<\/(p|div)>\s*<(p|div)[^>]*>/gi, '<br>');
        text = text.replace(/<(p|div)[^>]*>/gi, '');
        text = text.replace(/<\/(p|div)>/gi, '<br>');
        text = text.replace(/\r?\n/g, '<br>');
        text = text.replace(/(<br\s*\/?>\s*)+$/gi, '');

        protectedBlocks.forEach(function(block, i) {
            text = text.replace('\x00BLOCK' + i + '\x00', block);
        });

        return text;
    }
}

// Singleton instance
const formatter = new MessageFormatter();

export default formatter;



// WEBPACK FOOTER //
// ./src/utils/formatter.js